import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Card,
  CardContent,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import {
  Brain,
  Target,
  Layers,
  Cpu,
  MemoryStick,
  Zap,
  Sparkles,
  ExternalLink,
  CheckCircle2,
  AlertCircle,
  Database,
  BookOpen,
  Scale,
  Rocket,
  Shield,
  Copy,
  Check,
  Box,
  Shapes,
  PersonStanding,
  Video,
  ScanText,
} from 'lucide-react';
import { useState } from 'react';
import {
  TAO6PretrainedModel,
  TAO6TaskType,
  getModelCardInfo,
} from '@/mock/gpu-instance';

interface TAO6ModelCardProps {
  model: TAO6PretrainedModel | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelectModel?: (model: TAO6PretrainedModel) => void;
}

const getTaskIcon = (task: TAO6TaskType) => {
  switch (task) {
    case 'object_detection':
      return <Target className="h-5 w-5 text-green-500" />;
    case 'image_classification':
      return <Layers className="h-5 w-5 text-blue-500" />;
    case 'instance_segmentation':
      return <Box className="h-5 w-5 text-purple-500" />;
    case 'semantic_segmentation':
      return <Shapes className="h-5 w-5 text-cyan-500" />;
    case 'pose_estimation':
      return <PersonStanding className="h-5 w-5 text-orange-500" />;
    case 'action_recognition':
      return <Video className="h-5 w-5 text-pink-500" />;
    case 'ocr':
      return <ScanText className="h-5 w-5 text-amber-500" />;
  }
};

const getTaskBadge = (task: TAO6TaskType) => {
  switch (task) {
    case 'object_detection':
      return (
        <Badge className="bg-green-500/10 text-green-600 border-green-500/30">
          <Target className="h-3 w-3 mr-1" /> Object Detection
        </Badge>
      );
    case 'image_classification':
      return (
        <Badge className="bg-blue-500/10 text-blue-600 border-blue-500/30">
          <Layers className="h-3 w-3 mr-1" /> Image Classification
        </Badge>
      );
    case 'instance_segmentation':
      return (
        <Badge className="bg-purple-500/10 text-purple-600 border-purple-500/30">
          <Box className="h-3 w-3 mr-1" /> Instance Segmentation
        </Badge>
      );
    case 'semantic_segmentation':
      return (
        <Badge className="bg-cyan-500/10 text-cyan-600 border-cyan-500/30">
          <Shapes className="h-3 w-3 mr-1" /> Semantic Segmentation
        </Badge>
      );
    case 'pose_estimation':
      return (
        <Badge className="bg-orange-500/10 text-orange-600 border-orange-500/30">
          <PersonStanding className="h-3 w-3 mr-1" /> Pose Estimation
        </Badge>
      );
    case 'action_recognition':
      return (
        <Badge className="bg-pink-500/10 text-pink-600 border-pink-500/30">
          <Video className="h-3 w-3 mr-1" /> Action Recognition
        </Badge>
      );
    case 'ocr':
      return (
        <Badge className="bg-amber-500/10 text-amber-600 border-amber-500/30">
          <ScanText className="h-3 w-3 mr-1" /> OCR
        </Badge>
      );
  }
};

const TAO6ModelCard = ({
  model,
  open,
  onOpenChange,
  onSelectModel,
}: TAO6ModelCardProps) => {
  const [copiedPath, setCopiedPath] = useState(false);

  if (!model) return null;

  // Get model with default card info filled in
  const modelInfo = getModelCardInfo(model);

  const handleCopy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedPath(true);
      setTimeout(() => setCopiedPath(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleSelect = () => {
    onSelectModel?.(model);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-xl bg-gradient-to-br from-green-500/10 to-blue-500/10">
                <Brain className="h-8 w-8 text-primary" />
              </div>
              <div>
                <DialogTitle className="text-xl">{modelInfo.displayName}</DialogTitle>
                <DialogDescription className="mt-1">
                  {modelInfo.description}
                </DialogDescription>
              </div>
            </div>
          </div>
        </DialogHeader>

        {/* Task & Badges */}
        <div className="flex flex-wrap items-center gap-2 mt-2">
          {getTaskBadge(modelInfo.task)}
          {modelInfo.supportsQAT && (
            <Badge className="bg-yellow-500/10 text-yellow-600 border-yellow-500/30">
              <Sparkles className="h-3 w-3 mr-1" />
              QAT Ready
            </Badge>
          )}
          {modelInfo.supportsKD && (
            <Badge className="bg-purple-500/10 text-purple-600 border-purple-500/30">
              <Brain className="h-3 w-3 mr-1" />
              Knowledge Distillation
            </Badge>
          )}
          <Badge variant="outline" className="font-mono text-xs">
            {modelInfo.parameters} params
          </Badge>
        </div>

        <Separator className="my-4" />

        {/* Performance Metrics */}
        <div>
          <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
            <Zap className="h-4 w-4 text-yellow-500" />
            Performance Metrics
          </h3>
          <div className="grid grid-cols-4 gap-3">
            <Card className="bg-muted/50">
              <CardContent className="pt-3 pb-3 text-center">
                <div className="text-xs text-muted-foreground mb-1">
                  {modelInfo.accuracyMetric === 'mAP' ? 'mAP' : 'Top-1 Accuracy'}
                </div>
                <div className="text-2xl font-bold text-green-600">
                  {modelInfo.accuracy.toFixed(1)}%
                </div>
              </CardContent>
            </Card>
            <Card className="bg-muted/50">
              <CardContent className="pt-3 pb-3 text-center">
                <div className="text-xs text-muted-foreground mb-1 flex items-center justify-center gap-1">
                  <MemoryStick className="h-3 w-3" />
                  VRAM Required
                </div>
                <div className="text-2xl font-bold">
                  {modelInfo.vramRequired} GB
                </div>
              </CardContent>
            </Card>
            <Card className="bg-muted/50">
              <CardContent className="pt-3 pb-3 text-center">
                <div className="text-xs text-muted-foreground mb-1 flex items-center justify-center gap-1">
                  <Cpu className="h-3 w-3" />
                  Parameters
                </div>
                <div className="text-2xl font-bold">
                  {modelInfo.parameters}
                </div>
              </CardContent>
            </Card>
            <Card className="bg-muted/50">
              <CardContent className="pt-3 pb-3 text-center">
                <div className="text-xs text-muted-foreground mb-1">
                  Input Size
                </div>
                <div className="text-xl font-bold font-mono">
                  {modelInfo.inputSize}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        <Separator className="my-4" />

        {/* Pretrained Information */}
        <div>
          <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
            <Database className="h-4 w-4 text-blue-500" />
            Pretrained Information
          </h3>
          <div className="grid grid-cols-2 gap-4">
            <Card className="bg-muted/30">
              <CardContent className="pt-3 pb-3">
                <div className="text-xs text-muted-foreground mb-1">Pretrained Dataset</div>
                <div className="font-medium">{modelInfo.pretrainedDataset}</div>
                <div className="text-xs text-muted-foreground mt-1">
                  {modelInfo.pretrainedDataset?.includes('COCO') && 'Object detection benchmark'}
                  {modelInfo.pretrainedDataset?.includes('ImageNet') && 'Image classification benchmark'}
                </div>
              </CardContent>
            </Card>
            <Card className="bg-muted/30">
              <CardContent className="pt-3 pb-3">
                <div className="text-xs text-muted-foreground mb-1">Pretrained Classes</div>
                <div className="font-medium text-lg">
                  {modelInfo.pretrainedClasses?.toLocaleString() || 'N/A'}
                  <span className="text-xs text-muted-foreground ml-1">classes</span>
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  {modelInfo.task === 'object_detection' && 'Object categories for detection'}
                  {modelInfo.task === 'image_classification' && 'Image categories for classification'}
                  {modelInfo.task === 'instance_segmentation' && 'Object categories with masks'}
                  {modelInfo.task === 'semantic_segmentation' && 'Scene categories for segmentation'}
                  {modelInfo.task === 'pose_estimation' && 'Body keypoints'}
                  {modelInfo.task === 'action_recognition' && 'Action categories in video'}
                  {modelInfo.task === 'ocr' && 'Characters/text classes'}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        <Separator className="my-4" />

        {/* Use Cases */}
        <div>
          <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
            <Rocket className="h-4 w-4 text-purple-500" />
            Recommended Use Cases
          </h3>
          <div className="flex flex-wrap gap-2">
            {modelInfo.useCases?.map((useCase, idx) => (
              <Badge key={idx} variant="secondary" className="text-sm">
                {useCase}
              </Badge>
            ))}
          </div>
        </div>

        <Separator className="my-4" />

        {/* Strengths & Limitations */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-green-500" />
              Strengths
            </h3>
            <ul className="space-y-2">
              {modelInfo.strengths?.map((strength, idx) => (
                <li key={idx} className="flex items-start gap-2 text-sm">
                  <CheckCircle2 className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                  <span>{strength}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
              <AlertCircle className="h-4 w-4 text-yellow-500" />
              Limitations
            </h3>
            <ul className="space-y-2">
              {modelInfo.limitations?.map((limitation, idx) => (
                <li key={idx} className="flex items-start gap-2 text-sm">
                  <AlertCircle className="h-4 w-4 text-yellow-500 mt-0.5 shrink-0" />
                  <span>{limitation}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <Separator className="my-4" />

        {/* Supported Augmentations */}
        <div>
          <h3 className="text-sm font-semibold mb-3">Supported Augmentations</h3>
          <div className="flex flex-wrap gap-2">
            {modelInfo.supportedAugmentations.map((aug) => (
              <Badge key={aug} variant="outline" className="text-xs font-mono">
                {aug}
              </Badge>
            ))}
          </div>
        </div>

        <Separator className="my-4" />

        {/* Technical Details */}
        <div>
          <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
            <BookOpen className="h-4 w-4 text-gray-500" />
            Technical Details
          </h3>
          <div className="space-y-2">
            <div className="flex items-center justify-between bg-muted/50 p-2 rounded">
              <span className="text-sm text-muted-foreground">NGC Path</span>
              <div className="flex items-center gap-2">
                <code className="text-sm font-mono">{modelInfo.ngcPath}</code>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-6 w-6 p-0"
                  onClick={() => handleCopy(modelInfo.ngcPath)}
                >
                  {copiedPath ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                </Button>
              </div>
            </div>
            <div className="flex items-center justify-between bg-muted/50 p-2 rounded">
              <span className="text-sm text-muted-foreground">Backbone</span>
              <code className="text-sm font-mono">{modelInfo.backbone}</code>
            </div>
            <div className="flex items-center justify-between bg-muted/50 p-2 rounded">
              <span className="text-sm text-muted-foreground flex items-center gap-1">
                <Scale className="h-3 w-3" />
                License
              </span>
              <Badge variant="outline" className="text-xs">
                {modelInfo.license}
              </Badge>
            </div>
            {modelInfo.paperReference && (
              <div className="flex items-center justify-between bg-muted/50 p-2 rounded">
                <span className="text-sm text-muted-foreground">Paper</span>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-6 text-xs"
                  onClick={() => window.open(modelInfo.paperReference, '_blank')}
                >
                  <ExternalLink className="h-3 w-3 mr-1" />
                  View Paper
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-between mt-6 pt-4 border-t">
          <Button
            variant="outline"
            onClick={() =>
              window.open(
                `https://catalog.ngc.nvidia.com/orgs/nvidia/teams/tao/models/${modelInfo.name}`,
                '_blank'
              )
            }
          >
            <ExternalLink className="h-4 w-4 mr-2" />
            View on NGC
          </Button>
          {onSelectModel && (
            <Button onClick={handleSelect} className="bg-green-600 hover:bg-green-700">
              <CheckCircle2 className="h-4 w-4 mr-2" />
              Select Model
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TAO6ModelCard;
